import CommentList from './Comment';
import { PostActionBar } from './ActionBar';
import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import UserLink from '../UserLink';
import MediaFrame from './MediaFrame';
import {
    buildExploreLocationPath,
    defaultPfpUrl,
    formatAuthorAffiliation,
    formatRelativeTimestamp,
    formatShortLocation,
} from '../utils';
import { ExpandableFormattedBody } from '../ui/expandable-formatted-body';
import PostConnectButton from './PostConnectButton';
import ContentOverflowMenu from './ContentOverflowMenu';
import { useAdminModeration } from '../../context/AdminModerationContext';
import { GlobeIcon } from '../../widgets/icons';

/**
 * Post header: author, role · org, timestamp and location.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function PostHeader({ userId, user, post, showContent, setShowContent, toggleShowContent, onDeleted }) {
    const moderation = useAdminModeration();
    const handleToggleShowContent = () => {
        setShowContent(!showContent);
    };

    const relativeTime = formatRelativeTimestamp(post.created_at || post.timestamp);
    const shortLocation = formatShortLocation(post.location);
    const affiliationLine = formatAuthorAffiliation(post);
    const lat = Number(post.latitude);
    const lon = Number(post.longitude);
    const hasMapLocation =
        Boolean(shortLocation) &&
        Number.isFinite(lat) &&
        Number.isFinite(lon) &&
        !(lat === 0 && lon === 0);
    const explorePath = hasMapLocation
        ? buildExploreLocationPath(lat, lon, shortLocation)
        : "";

    return (
        <div className="flex items-center gap-3">
            <UserLink
                userId={post.author_id}
                isOrg={!!post.author_is_org}
                showProfilePic={true}
                profilePicUrl={post.author_pfp || defaultPfpUrl}
                profilePicSize="h-11 w-11"
                className="flex-shrink-0"
            />
            <div className="min-w-0 flex-1">
                <UserLink
                    userId={post.author_id}
                    isOrg={!!post.author_is_org}
                    className="block truncate text-post font-emphasis text-text-primary hover:text-accent"
                >
                    {post.author_name || "Author"}
                </UserLink>
                {affiliationLine ? (
                    <div className="mt-0.5 truncate text-caption text-text-muted">
                        {affiliationLine}
                    </div>
                ) : null}
                {relativeTime || shortLocation ? (
                    <div className="mt-1 flex min-w-0 items-center gap-1.5 text-text-faint">
                        {relativeTime ? (
                            <span className="shrink-0 font-ui text-meta uppercase tracking-label">
                                {relativeTime}
                            </span>
                        ) : null}
                        {shortLocation ? (
                            <>
                                {relativeTime ? <span aria-hidden>·</span> : null}
                                {hasMapLocation ? (
                                    <Link
                                        to={explorePath}
                                        className="min-w-0 truncate text-meta text-text-faint hover:text-accent hover:underline"
                                    >
                                        {shortLocation}
                                    </Link>
                                ) : (
                                    <span className="min-w-0 truncate text-meta">
                                        {shortLocation}
                                    </span>
                                )}
                            </>
                        ) : null}
                    </div>
                ) : null}
            </div>
            <div className="flex shrink-0 items-center gap-1">
            {!toggleShowContent && !moderation ? (
                <PostConnectButton userId={userId} user={user} item={post} />
            ) : null}
            {toggleShowContent ? (
                <button
                    type="button"
                    className="text-compact text-accent hover:text-accent-hover"
                    onClick={handleToggleShowContent}
                >
                    {showContent ? "Hide Content" : "Show Content"}
                </button>
            ) : null}
            <ContentOverflowMenu
                userId={userId}
                user={user}
                item={post}
                contentType="post"
                onDeleted={onDeleted}
            />
            </div>
        </div>
    );
}

/**
 * Feed post card.
 * @param {Object} props
 * @returns {JSX.Element}
 */
function Post({ userId, user, post, hideContent, focusCommentId = null, onDeleted }) {
    const moderation = useAdminModeration();
    const [showContent, setShowContent] = useState(!hideContent);
    const [pendingComment, setPendingComment] = useState(null);
    const [removed, setRemoved] = useState(false);

    const handleCommentCreated = (comment) => {
        setPendingComment(comment);
    };

    const handlePendingCommentConsumed = () => {
        setPendingComment(null);
    };

    const handleDeleted = () => {
        setRemoved(true);
        onDeleted?.();
    };

    if (removed) {
        return null;
    }

    return (
        <div className="post min-w-0 overflow-x-hidden rounded-card border border-ui-border bg-ui-card p-5 shadow-shell-sm">
            <PostHeader
                userId={userId}
                user={user}
                post={post}
                showContent={showContent}
                setShowContent={setShowContent}
                toggleShowContent={hideContent}
                onDeleted={handleDeleted}
            />
            <div className="post-body mt-3.5">
                <ExpandableFormattedBody as="p" className="text-post text-text-primary" mentions={post.mentions}>
                    {post.caption}
                </ExpandableFormattedBody>
                {showContent && (
                    <MediaFrame post={post} />
                )}
            </div>
            {showContent && !moderation ? (
                <PostActionBar
                    userId={userId}
                    user={user}
                    post={post}
                    onCommentCreated={handleCommentCreated}
                />
            ) : null}
            {showContent && (
                <CommentList
                    userId={userId}
                    user={user}
                    post={post}
                    contentType="post"
                    pendingComment={pendingComment}
                    onPendingCommentConsumed={handlePendingCommentConsumed}
                    focusCommentId={focusCommentId}
                />
            )}
        </div>
    );
}

export default Post;
